import { Table, TableColumnsType } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from "react-router-dom";
import { deleteOceanStudent, getRegularOcean, getRegularOceanStudents } from "../../../api/oceanManagement.api";
import { getOceanResultUrl, getPredigerHistoryCode } from '../../../api/oceanTest.api';
import Button from "../../../components/Button";
import AddStudentModal from "../../../components/class/ocean/modals/AddStudentModal";
import RegularEditor from "../../../components/class/ocean/RegularEditor";
import ErrorResponse from "../../../components/ErrorResponse";
import SimpleHeader from "../../../components/layout/SimpleHeader";
import Loader from "../../../components/Loader";
import Pagination from "../../../components/Pagination";
import useAuth from "../../../hooks/useAuth";
import useAxios from "../../../hooks/useAxios";
import useNotification from "../../../hooks/useNotification";
import config from "../../../lib/config";
import { IGetRegularOcean, IGetRegularOceanStudents, IRegularOceanStudent } from "../../../models/oceanManagement.model";
import { IGetOceanUrl } from "../../../models/oceanTest.model";
import { RootState } from "../../../store/rootReducer";
import { getWebUrl } from '../../../lib/util';

interface IMatch {
  scheduleCode: string;
}

const OceanEdit = () => {
  const { t } = useTranslation();
  const match = useRouteMatch<IMatch>();
  const scheduleCode = Number(match.params.scheduleCode);
  const { writeAuthYn } = useAuth();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetRegularOcean>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetRegularOceanStudents>();
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetOceanUrl>();
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError, execute: deleteExecute } = useAxios();
  const { data: predigerResponse, isLoading: isPredigerLoading, error: predigerError, execute: predigerExecute } = useAxios<any>();

  const [pageNo, setPageNo] = useState(1);
  const [addStudentVisible, setAddStudentVisible] = useState(false);


  useEffect(() => {
    if (predigerResponse && !isPredigerLoading) {
      const left = (window.screen.width - 1024) / 2;
      const top = (window.screen.height - 876) / 2;
      window.open(`${getWebUrl()}/prediger/${predigerResponse?.data}`, `prediger/${predigerResponse?.data}`, `width=1024, height=876, top=${top}, left=${left}, fullscreen=no, menubar=no, status=no, toolbar=no, titlebar=yes, location=no, scrollbar=no`);
    }
  }, [predigerResponse, isPredigerLoading])

  useEffect(() => {
    if (scheduleCode) {
      execute(getRegularOcean(scheduleCode));
      doGetRegularOceanStudents();
    }
  }, [scheduleCode]);

  useEffect(() => {
    if (res3 && !isLoading3) {
      window.open(`${res3?.data}&employeeMemberCode=${auth.memberCode}`, '_blank', 'width=1200, height=768, top=100, left=100, fullscreen=no, menubar=no, status=no, toolbar=no, titlebar=yes, location=no, scrollbar=no');
    }
  }, [res3, isLoading3]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      doGetRegularOceanStudents();
      successNotification(t('common.message.delete'));
    }
  }, [deleteRes, deleteIsLoading]);

  const doGetRegularOceanStudents = () => {
    if (scheduleCode) {
      execute2(getRegularOceanStudents(scheduleCode));
    }
  }

  const onDeleteStudent = (testNo: number) => {
   deleteExecute(deleteOceanStudent(testNo));
  };

  const onOpenOceanResultPopup = (oceanTestCode: number, eduStepCode: string) => {
    if (eduStepCode === "MD") {
      predigerExecute(getPredigerHistoryCode(oceanTestCode));
      return;
    }

    execute3(getOceanResultUrl(oceanTestCode));
  }

  const columns: TableColumnsType<IRegularOceanStudent> = [
    {
      title: t('class.ocean.column1'),
      dataIndex: 'memKorName',
      key: 'memKorName',
      align: 'center',
    },
    {
      title: t('class.ocean.column6'),
      dataIndex: 'testCompleteDateTime',
      key: 'testCompleteDateTime',
      align: 'center',
      render: value => value ? moment(value).format('YYYY.MM.DD HH:mm:ss') : '-',
    },
    {
      title: t('common.button.delete'),
      key: 'testNo',
      dataIndex: 'testNo',
      align: 'center',
      render: (value, record) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            onClick={() => onDeleteStudent(value)}
            className="btn-basic small gray"
            text={t('common.button.delete')}
            disabled={record.oceanResult !== 'N'}
            writeAuthYn={writeAuthYn}
          />
        </div>
      ),
    },
    {
      title: t('class.ocean.column7'),
      key: 'oceanResult',
      dataIndex: 'oceanResult',
      align: 'center',
      render: (value, record) => value === 'Y'
        ? (
          <div className="comm-btn__area justify-content-center">
            <Button
              onClick={() => onOpenOceanResultPopup(record.testNo, record.eduStepCode)}
              className="btn-basic small pink-line"
              text={t('class.ocean.button2')}
              writeAuthYn={writeAuthYn}
            />
          </div>
          )
        : value
    },

  ];

  return (
    <div className="content scrollbar">
      {(isLoading || isLoading2 || isLoading3 || deleteIsLoading) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={deleteError} />
      <SimpleHeader
        iconFile="icon-student-big.svg"
        parentMenu={t('common.menu.class')}
        currentMenu={t('class.ocean.title2')}
      />

      <RegularEditor
        scheduleCode={scheduleCode}
        data={res?.data}
      />
      
      <div className="comm-bx__between">
        <h3 className="sub-title">{t('class.ocean.title3')}</h3>
        <div className="comm-btn__area">
          <Button
            onClick={() => setAddStudentVisible(true)}
            className="btn-basic pink-line"
            text={t('class.ocean.button3')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>

      <div className='comm-table__area'>
        <Table
          columns={columns}
          dataSource={res2?.data}
          pagination={{
            current: pageNo,
            pageSize: config.pageSize,
          }}
        />
      </div>

      <div className="comm-bx__between">
        <div className="common-paging center">
          <Pagination
            count={res2?.data?.length || 0}
            page={pageNo}
            setPage={setPageNo}
          />
        </div>
      </div>

      {addStudentVisible &&
        <AddStudentModal
          visible={addStudentVisible}
          setVisible={setAddStudentVisible}
          campusCode={res?.data?.campusCode}
          scheduleCode={scheduleCode}
          srtDate={res?.data?.srtDate}
          endDate={res?.data?.endDate}
          doGetOceanStudents={doGetRegularOceanStudents}
        />
      }
    </div>
  );
}

export default OceanEdit;
